import { Check, Lock, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Textarea } from '@/components/ui/Textarea';
import {
  activeHighlightTags,
  highlightTagColorLabelKey,
  highlightTagDisplayName,
  highlightTagInkCss,
  type HighlightTagColorSource,
} from '@/lib/highlightTagColors';
import { fileCategorySwatchClass } from '@/lib/patientFileCategories';
import { cn } from '@/utils/cn';
import {
  HIGHLIGHT_COLORS,
  HIGHLIGHT_COMMENT_MAX,
  highlightColorCssVar,
  highlightColorKey,
  type HighlightColor,
  type HighlightVisibility,
} from '@/utils/fileHighlights';

export function FileHighlightSwatches({
  color,
  onColor,
  disabled = false,
}: {
  color: HighlightColor;
  onColor: (color: HighlightColor) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="fp-swatches" role="radiogroup" aria-label={t('documents.highlights.color')}>
      {HIGHLIGHT_COLORS.map((value) => {
        const on = color === value;
        const label = t(highlightColorKey(value));
        return (
          <button
            key={value}
            type="button"
            role="radio"
            className="fp-swatch"
            style={{ ['--hc' as string]: highlightColorCssVar(value) }}
            aria-label={label}
            title={label}
            aria-pressed={on}
            aria-checked={on}
            disabled={disabled}
            onClick={() => onColor(value)}
          >
            <span className="fp-swatch-dot">{on ? <Check className="h-3.5 w-3.5" /> : null}</span>
          </button>
        );
      })}
    </div>
  );
}

export function FileHighlightTagChoices({
  tags,
  selectedIds,
  disabled = false,
  onChange,
}: {
  tags: readonly HighlightTagColorSource[];
  selectedIds: readonly string[];
  disabled?: boolean;
  onChange?: (ids: string[]) => void;
}) {
  const { t } = useTranslation();
  const ordered = activeHighlightTags(tags);
  const selected = new Set(selectedIds);
  const unnamed = t('settings.highlight_tags.unnamed');
  return (
    <div className="fp-tag-choices" role="group" aria-label={t('documents.highlights.tags')}>
      {ordered.map((tag) => {
        const on = selected.has(tag.id);
        const colorLabel = t(highlightTagColorLabelKey(tag.color_token));
        const label = highlightTagDisplayName(tag, unnamed, colorLabel);
        const ink = highlightTagInkCss(tag.color_token);
        return (
          <button
            key={tag.id}
            type="button"
            className={cn('fp-tag-choice', on && 'is-on')}
            aria-pressed={on}
            aria-label={label}
            disabled={disabled}
            onClick={() => {
              if (!onChange) return;
              const next = on
                ? selectedIds.filter((id) => id !== tag.id)
                : [...selectedIds, tag.id];
              onChange(next);
            }}
          >
            <i
              className={cn(!ink && fileCategorySwatchClass(tag.color_token))}
              style={ink ? { background: ink } : undefined}
              aria-hidden
            />
            <span className={cn(!tag.name.trim() && 'is-unnamed')}>{label}</span>
            {on ? <Check className="fp-tag-check h-3 w-3" aria-hidden /> : null}
          </button>
        );
      })}
    </div>
  );
}

export function FileHighlightFields({
  color,
  visibility,
  comment,
  disabled = false,
  commentId,
  tags = [],
  selectedTagIds = [],
  onColor,
  onSelectedTagIds,
  onVisibility,
  onComment,
}: {
  color: HighlightColor;
  visibility: HighlightVisibility;
  comment: string;
  disabled?: boolean;
  commentId: string;
  tags?: readonly HighlightTagColorSource[];
  selectedTagIds?: readonly string[];
  onColor: (color: HighlightColor) => void;
  onSelectedTagIds?: (ids: string[]) => void;
  onVisibility: (visibility: HighlightVisibility) => void;
  onComment: (comment: string) => void;
}) {
  const { t } = useTranslation();
  const choices = activeHighlightTags(tags);
  return (
    <>
      {choices.length > 0 ? (
        <div className="fp-field">
          <span className="fp-field-label">{t('documents.highlights.tags')}</span>
          <FileHighlightTagChoices
            tags={choices}
            selectedIds={selectedTagIds}
            disabled={disabled}
            onChange={onSelectedTagIds}
          />
        </div>
      ) : (
        <div className="fp-field">
          <span className="fp-field-label">{t('documents.highlights.color')}</span>
          <FileHighlightSwatches color={color} onColor={onColor} disabled={disabled} />
        </div>
      )}
      <div className="fp-field">
        <label className="fp-field-label" htmlFor={commentId}>
          {t('documents.highlights.comment')}{' '}
          <span className="fp-field-opt">{t('documents.highlights.comment_optional')}</span>
        </label>
        <Textarea
          id={commentId}
          className="fp-comment"
          value={comment}
          maxLength={HIGHLIGHT_COMMENT_MAX}
          disabled={disabled}
          rows={3}
          placeholder={t('documents.highlights.comment_placeholder')}
          aria-label={t('documents.highlights.comment')}
          onChange={(event) => onComment(event.target.value.slice(0, HIGHLIGHT_COMMENT_MAX))}
        />
        <div className="fp-count">
          {t('documents.highlights.comment_count', { count: comment.length })}
        </div>
      </div>
      <div className="fp-field">
        <span className="fp-field-label">{t('documents.highlights.visibility')}</span>
        <div
          className="fp-vis-seg clinio-seg"
          role="radiogroup"
          aria-label={t('documents.highlights.visibility')}
        >
          <button
            type="button"
            aria-pressed={visibility === 'private'}
            disabled={disabled}
            onClick={() => onVisibility('private')}
          >
            <Lock className="h-3.5 w-3.5" aria-hidden />
            {t('documents.highlights.private')}
          </button>
          <button
            type="button"
            aria-pressed={visibility === 'clinic'}
            disabled={disabled}
            onClick={() => onVisibility('clinic')}
          >
            <Users className="h-3.5 w-3.5" aria-hidden />
            {t('documents.highlights.shared')}
          </button>
        </div>
      </div>
    </>
  );
}
